import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, Filter, Wrench, Settings, MapPin, User, Calendar, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Tool } from '../types';
import ToolCard from '../components/tools/ToolCard';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';

const ToolsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('filter') || 'all');
  const [locationFilter, setLocationFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [lastUpdated] = useState(new Date().toLocaleString());

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setStatusFilter(filter);
      setIsFiltersVisible(true);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Parse images string to array for each tool
      const parsedTools = (data || []).map(tool => ({
        ...tool,
        images: tool.images ? tool.images.split(',').map((url: string) => url.trim()).filter((url: string) => url) : []
      }));

      setTools(parsedTools);
    } catch (error) {
      console.error('Error loading tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTool = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this tool? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('tools')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setTools(prev => prev.filter(tool => tool.id !== id));
      } catch (error) {
        console.error('Error deleting tool:', error);
      }
    }
  };

  // Filter tools based on search and filters
  const filteredTools = tools.filter(tool => {
    const matchesSearch = 
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.project_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.assigned_to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tool.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' ||
      tool.status === statusFilter;
    
    const matchesLocation = !locationFilter || 
      tool.location.toLowerCase().includes(locationFilter.toLowerCase());
    
    const matchesProject = !projectFilter || 
      tool.project_code?.toLowerCase().includes(projectFilter.toLowerCase());
    
    return matchesSearch && matchesStatus && matchesLocation && matchesProject;
  });

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('filter');
      } else {
        params.set('filter', value);
      }
      return params;
    });
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'available', label: 'Available' },
    { value: 'in_use', label: 'In Use' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'retired', label: 'Retired' },
  ];

  // Calculate summary statistics
  const totalTools = tools.length;
  const availableTools = tools.filter(tool => tool.status === 'available').length;
  const inUseTools = tools.filter(tool => tool.status === 'in_use').length;
  const maintenanceTools = tools.filter(tool => tool.status === 'maintenance').length;
  const uniqueLocations = new Set(tools.map(tool => tool.location)).size;
  const activeProjects = new Set(tools.filter(tool => tool.project_code).map(tool => tool.project_code)).size;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Professional workshop with organized tools and equipment for research and development"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Tools Management</h1>
              <p className="text-lg opacity-90">
                Comprehensive tool tracking and equipment management system
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Wrench className="h-4 w-4 mr-2" />
                  <span>{totalTools} Total Tools</span>
                </div>
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  <span>{availableTools} Available</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>{uniqueLocations} Locations</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last Updated: {lastUpdated}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Link to="/tools/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Tool
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-full mr-3">
              <Wrench className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Tools</p>
              <p className="text-2xl font-bold text-gray-900">{totalTools}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-50 rounded-full mr-3">
              <Settings className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Available</p>
              <p className="text-2xl font-bold text-gray-900">{availableTools}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-50 rounded-full mr-3">
              <User className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">In Use</p>
              <p className="text-2xl font-bold text-gray-900">{inUseTools}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-50 rounded-full mr-3">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Maintenance</p>
              <p className="text-2xl font-bold text-gray-900">{maintenanceTools}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-50 rounded-full mr-3">
              <MapPin className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Locations</p>
              <p className="text-2xl font-bold text-gray-900">{uniqueLocations}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-50 rounded-full mr-3">
              <Calendar className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Projects</p>
              <p className="text-2xl font-bold text-gray-900">{activeProjects}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-600" />
          </div>
          <Input
            type="search"
            placeholder="Search tools, serial numbers, projects, assignments..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
        </div>
        <Button 
          variant="secondary" 
          className="w-full sm:w-auto"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
        </Button>
      </div>

      {isFiltersVisible && (
        <div className="flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Input
              label="Location"
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              fullWidth
            />
          </div>
          <div className="flex-1">
            <Input
              label="Project Code"
              placeholder="Filter by project..."
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              fullWidth
            />
          </div>
        </div>
      )}

      {/* Tools Grid */}
      {filteredTools.length === 0 ? (
        loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Wrench className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No tools found</p>
            <Link to="/tools/new">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Tool
              </Button>
            </Link>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTools.map(tool => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onDelete={handleDeleteTool}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolsPage;